import React, { useMemo } from 'react'
import type { QuoteFile } from 'paintscout'
import { getImageWidth, getColumnWidth } from './filesGridLayout'
import {
  createStyles,
  withStyles,
  Tooltip,
  makeStyles,
  Button,
  FilePreview,
  Grid,
  Dropzone,
  Spinner,
  useTranslation
} from '@paintscout/ui'
import type { WithStyles, MuiTheme as Theme, DropzoneProps } from '@paintscout/ui'
import PictureAsPdfOutlinedIcon from '@material-ui/icons/PictureAsPdfOutlined'
import { useUser } from '@paintscout/ui-business'

import { SortableContainer, SortableElement, arrayMove } from 'react-sortable-hoc'
import type { SortableContainerProps, SortableElementProps } from 'react-sortable-hoc'
import { useConnection } from 'react-detect-offline'
import { TRIAL_QUOTE_FILES_LIMIT } from '@paintscout/util'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'

import { getFiles } from '@paintscout/util/builder'
import classnames from 'classnames'
import { useQuote } from '../context'

export interface FilesGridProps {
  files: QuoteFile[]
  editable?: boolean
  showUploadDropzone?: boolean
  responsiveTransformation?: boolean
  columns?: number
  showBorder?: boolean
  justify?: 'space-around' | 'space-between' | 'center' | 'flex-start' | 'flex-end'
  isLoading?: boolean
  /**
   * Reorder auto-scroll only works if react-sortable-hoc knows the real scroll container.
   * On the full-page estimate/invoice the page scrolls at the window level (no `overflow:auto`
   * ancestor), so autoscroll can't reach off-screen drop targets on touch. Set this true in that
   * context to use the window as the scroll container. Leave false inside dialogs, where the
   * `DialogContent` (overflow-y:auto) is found automatically (#21264).
   */
  useWindowAsScrollContainer?: boolean

  onReorder?: (files: QuoteFile[]) => any
  onUpload?: (files: File[]) => any
  onFileClick?: (index: number) => any
  onLoadMore?: () => any
}

type FilesGridPropsWithInjections = FilesGridProps & WithStyles<typeof styles>

const styles = createStyles({
  dragging: {
    zIndex: 2147483002
  },
  grid3: {
    '@media print': {
      width: '25%',
      flexBasis: 'unset'
    }
  },
  grid6: {
    '@media print': {
      width: '50%',
      flexBasis: 'unset'
    }
  }
})

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  uploadButton: {},
  grid3: {
    '@media print': {
      width: '25%',
      flexBasis: 'unset'
    }
  },
  grid4: {
    '@media print': {
      width: '33.333%',
      flexBasis: 'unset'
    }
  },
  grid6: {
    '@media print': {
      width: '50%',
      flexBasis: 'unset'
    }
  },
  grid12: {
    '@media print': {
      width: '100%',
      flexBasis: 'unset'
    }
  },
  tile: ({ showBorder }: { showBorder: boolean }) => ({
    position: 'relative',
    width: '100%',
    aspectRatio: '4/3',
    borderRadius: theme.borderRadius.md,
    overflow: 'hidden',
    border: showBorder === false ? 'none' : `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    background: theme.palette.common.white,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    transition: 'border-color 120ms ease, box-shadow 120ms ease',
    // Suppress native image drag / text selection / iOS callout so the press gesture
    // reaches react-sortable-hoc instead of starting a native image drag (#20637).
    userSelect: 'none',
    WebkitUserSelect: 'none',
    WebkitTouchCallout: 'none',
    '& img': {
      WebkitUserDrag: 'none',
      userSelect: 'none'
    },
    '&:hover': {
      borderColor: theme.paintscout.palette.brand.Neutral.P70
    },
    '@media print': {
      pageBreakInside: 'avoid'
    }
  }),
  tilePdf: {
    background: theme.paintscout.palette.brand.Neutral.P96
  },
  tileSelected: {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 1px ${theme.palette.primary.main}`
  },
  filePreviewRoot: {
    padding: 0,
    width: '100%',
    height: '100%',
    minHeight: 0,
    minWidth: 0
  },
  filePreviewImageCover: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  imageMediaSlot: {
    flex: 1,
    minHeight: 0,
    width: '100%',
    overflow: 'hidden',
    display: 'flex'
  },
  imageOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: theme.spacing(1.5),
    background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.35) 60%, rgba(0,0,0,0) 100%)',
    color: theme.palette.common.white,
    pointerEvents: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25)
  },
  imageTitle: {
    ...theme.typography.subtitle2,
    color: theme.palette.common.white,
    fontWeight: theme.typography.fontWeightMedium,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  imageCaption: {
    ...theme.typography.caption,
    color: theme.palette.common.white,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden'
  },
  pdfPreviewSlot: {
    flex: '1 1 auto',
    minHeight: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    padding: theme.spacing(1.5, 1.5, 0.5, 1.5),
    '& canvas, & img': {
      maxWidth: '100%',
      maxHeight: '100%',
      objectFit: 'contain'
    }
  },
  pdfMeta: {
    flex: '0 0 auto',
    padding: theme.spacing(0.5, 1.5, 1, 1.5),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25)
  },
  pdfTitle: {
    ...theme.typography.caption,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.paintscout.palette.brand.Neutral.P30,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  pdfCaption: {
    ...theme.typography.caption,
    color: theme.paintscout.palette.brand.Neutral.P50,
    display: '-webkit-box',
    WebkitLineClamp: 1,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden'
  },
  pdfBadge: {
    ...theme.typography.caption,
    color: theme.paintscout.palette.brand.Neutral.P50,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5)
  },
  selected: {
    boxShadow: `0 0 0 2px ${theme.palette.primary.main} inset`
  },
  check: {
    position: 'absolute',
    bottom: 8,
    right: 8,
    zIndex: 10,
    color: '#fff',
    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.4))'
  },
  item: {
    '@media print': {
      pageBreakInside: 'avoid'
    }
  }
}))

function isPdfFile(file?: QuoteFile): boolean {
  if (!file) return false
  if (file.format === 'pdf' || file.type === 'application/pdf') return true
  if (!file.src) return false
  return file.src.split('?')[0]?.toLowerCase().endsWith('.pdf') ?? false
}

interface SortableFilePreviewProps extends SortableElementProps {
  file?: QuoteFile
  columns?: number
  showBorder: boolean
  classes?: any
  editable?: boolean
  onClick?: () => any
}

const SortableFilePreview: React.ComponentClass<SortableFilePreviewProps, any> = SortableElement(
  ({ file, columns, onClick, ...props }: SortableFilePreviewProps) => {
    const classes = useStyles(props)
    const { __ } = useTranslation()
    let transformationProps: any = {
      ...getImageWidth(columns),
      crop: 'pad',
      background: '#fff'
    }

    if ((file.type ?? 'image').startsWith('video')) {
      transformationProps = {
        ...transformationProps,
        controls: false
      }
    }

    const { columnWidth, columnClass } = getColumnWidth(columns, classes)
    const isPdf = isPdfFile(file)
    const hasOverlayText = !isPdf && !!(file?.title || file?.caption)

    return (
      <Grid
        item
        key={file.key}
        xs={12}
        sm={columnWidth}
        md={columnWidth}
        className={classnames(columnClass, classes.item)}
      >
        <div className={classnames(classes.tile, isPdf && classes.tilePdf, file.selected && classes.tileSelected)}>
          {file.selected && <CheckCircleIcon className={classes.check} />}
          {isPdf ? (
            <>
              <div className={classes.pdfPreviewSlot}>
                <FilePreview
                  classes={{ root: classes.filePreviewRoot }}
                  file={file}
                  videoThumbnail={true}
                  onClick={onClick}
                  {...transformationProps}
                />
              </div>
              <div className={classes.pdfMeta}>
                {file?.title && (
                  <div className={classes.pdfTitle} title={file.title}>
                    {file.title}
                  </div>
                )}
                {file?.caption && (
                  <div className={classes.pdfCaption} title={file.caption}>
                    {file.caption}
                  </div>
                )}
                <div className={classes.pdfBadge}>
                  <PictureAsPdfOutlinedIcon fontSize="small" />
                  {__('PDF')}
                </div>
              </div>
            </>
          ) : (
            <div className={classes.imageMediaSlot}>
              <FilePreview
                classes={{
                  root: classes.filePreviewRoot,
                  fileRoot: classes.filePreviewImageCover,
                  image: classes.filePreviewImageCover
                }}
                file={file}
                videoThumbnail={true}
                onClick={onClick}
                {...transformationProps}
              />
              {hasOverlayText && (
                <div className={classes.imageOverlay}>
                  {file?.title && (
                    <div className={classes.imageTitle} title={file.title}>
                      {file.title}
                    </div>
                  )}
                  {file?.caption && (
                    <div className={classes.imageCaption} title={file.caption}>
                      {file.caption}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </Grid>
    )
  }
)

interface SortableGridContainerProps extends SortableContainerProps {
  files: QuoteFile[]
  disabled: boolean
  showUploadDropzone?: boolean
  columns: number
  editable: boolean
  showBorder: boolean
  justify: 'space-around' | 'space-between' | 'center' | 'flex-start' | 'flex-end'
  classes?: any
  onUpload?: (files: File[]) => any
  onClick?: (index: number) => any
  onLoadMore?: () => Promise<boolean>
}

const SortableGridContainer: React.ComponentClass<SortableGridContainerProps, any> = SortableContainer(
  ({
    files,
    disabled,
    showUploadDropzone,
    columns,
    showBorder,
    editable,
    justify,
    onUpload,
    onClick,
    onLoadMore,
    classes
  }: SortableGridContainerProps) => {
    const { online } = useConnection()
    const { quote } = useQuote()
    const { isTrial } = useUser()
    const { __ } = useTranslation()
    const [loadingMore, setLoadingMore] = React.useState(false)
    // calculate initial number of quote files so we know how many files are left without needing
    // to update the quote first
    const numQuoteFiles = useMemo(() => getFiles({ quote, attached: true }).length - files.length, [quote])

    const canAddFiles = !isTrial || numQuoteFiles + files.length < TRIAL_QUOTE_FILES_LIMIT

    return (
      <Grid container spacing={1} alignItems={'stretch'} justifyContent={justify}>
        {files.map((file, index) => (
          <SortableFilePreview
            key={file.key}
            index={index}
            file={file}
            editable={editable}
            showBorder={showBorder}
            columns={columns}
            disabled={disabled}
            onClick={() => {
              if (onClick) {
                onClick(index)
              }
            }}
          />
        ))}
        {onLoadMore && (
          <Grid item key={'loadMore'} xs={12} sm={12} md={12} className={classes.grid6}>
            <Button
              onClick={async () => {
                setLoadingMore(true)
                await onLoadMore()
                setLoadingMore(false)
              }}
              variant="outlined"
              fullWidth
              loading={loadingMore}
              disabled={!online}
            >
              {__('Load More')}
            </Button>
          </Grid>
        )}
        {showUploadDropzone && (
          <Grid item key={'upload'} xs={12} sm={12} md={12} className={classes.grid6}>
            <Tooltip title={!canAddFiles ? __('You must upgrade your plan to add more media') : ''}>
              <div>
                <UploadDropzone onUpload={onUpload} disabled={!canAddFiles || !online || !editable} />
              </div>
            </Tooltip>
          </Grid>
        )}
      </Grid>
    )
  }
)

function UploadDropzone(props: { onUpload?: (files: File[]) => any } & Partial<DropzoneProps>) {
  const { __ } = useTranslation()

  return (
    <Dropzone
      text={__('Add Media')}
      onDropAccepted={props.onUpload}
      style={{ height: 'initial', marginTop: 20 }}
      {...props}
    />
  )
}

function FilesGridNext(props: FilesGridPropsWithInjections) {
  const {
    files,
    editable,
    showUploadDropzone,
    onUpload,
    columns = 4,
    showBorder = true,
    justify = 'flex-start',
    classes,
    onReorder,
    onFileClick,
    isLoading,
    onLoadMore,
    useWindowAsScrollContainer = false
  } = props

  function handleSortEnd({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) {
    const reorderedFiles = arrayMove(files, oldIndex, newIndex)

    if (onReorder) {
      onReorder(reorderedFiles)
    }
  }

  if (isLoading) {
    return <Spinner padded />
  }

  return (
    <>
      <SortableGridContainer
        classes={classes}
        helperClass={classes.dragging}
        pressDelay={150}
        useWindowAsScrollContainer={useWindowAsScrollContainer}
        files={files}
        editable={editable}
        axis="xy"
        columns={columns}
        showBorder={showBorder}
        justify={justify}
        onSortEnd={handleSortEnd}
        disabled={!editable}
        onClick={onFileClick}
        showUploadDropzone={showUploadDropzone}
        onUpload={onUpload}
        onLoadMore={onLoadMore}
      />
    </>
  )
}

export default withStyles(styles)(FilesGridNext)
